import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { languages } from '../languages';
import './LanguageSwitcher.css';

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    const options = dropdownRef.current?.querySelectorAll('.language-option');
    /* v8 ignore next -- dropdownRef is set and querySelectorAll never returns null */
    if (!options) return;

    const optionsArray = Array.from(options) as HTMLElement[];
    const currentIndex = optionsArray.findIndex(opt => opt === document.activeElement);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % optionsArray.length;
        optionsArray[nextIndex]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : optionsArray.length - 1;
        optionsArray[prevIndex]?.focus();
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        buttonRef.current?.focus();
        break;
      case 'Home':
        e.preventDefault();
        optionsArray[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        optionsArray[optionsArray.length - 1]?.focus();
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    window.requestAnimationFrame(() => {
      const firstOption = dropdownRef.current?.querySelector<HTMLElement>('.language-option');
      firstOption?.focus();
    });
  }, [isOpen]);

  return (
    <div className="language-picker" ref={dropdownRef} onKeyDown={handleKeyDown}>
      <button
        id="currentLanguage"
        ref={buttonRef}
        className="language-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={`Current language: ${currentLanguage.name}. Click to change language.`}
      >
        {currentLanguage.flag} <span className="lang-name">{currentLanguage.nativeName}</span>
      </button>

      <div
        id="languageDropdown"
        className={`language-dropdown ${isOpen ? 'show' : ''}`}
        role="menu"
        aria-label="Language options"
        hidden={!isOpen}
      >
        {languages.map((lang) => (
          <button
            key={lang.code}
            className={`language-option ${lang.code === i18n.language ? 'active' : ''}`}
            data-lang={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            role="menuitem"
            aria-label={`Switch to ${lang.name}`}
            aria-current={lang.code === i18n.language ? 'true' : undefined}
            tabIndex={isOpen ? 0 : -1}
          >
            {lang.flag} <span>{lang.nativeName}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
